import { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { getChartSeriesFromBlock } from '../../utils/periodizationValueSchema';
import { cn } from '../../lib/utils';

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

const AttributeTrendsPanel = ({ tree, types }) => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(() =>
    Object.fromEntries((types || []).slice(0, 4).map((ty) => [ty.type_key, true]))
  );

  const chartData = useMemo(() => {
    if (!tree?.phases) return [];
    const weekMap = new Map();
    for (const phase of tree.phases) {
      for (const micro of phase.microcycles || []) {
        const w = micro.week_number_in_block;
        if (!weekMap.has(w)) weekMap.set(w, { week: w, weekLabel: `W${w}` });
        const row = weekMap.get(w);
        for (const type of types || []) {
          if (!visible[type.type_key]) continue;
          const series = getChartSeriesFromBlock(tree, type.type_key);
          const pt = series.find((p) => p.week === w);
          if (pt?.numericValue != null) row[type.type_key] = pt.numericValue;
        }
      }
    }
    return [...weekMap.values()].sort((a, b) => a.week - b.week);
  }, [tree, types, visible]);

  const toggleSeries = (key) => {
    setVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!types?.length) return null;

  return (
    <Card className='w-full'>
      <CardHeader className='pb-2'>
        <CardTitle className='text-sm'>{t('periodization.trends.title')}</CardTitle>
        <p className='text-xs text-muted-foreground'>{t('periodization.trends.hint')}</p>
      </CardHeader>
      <CardContent className='flex flex-col flex-1 min-h-0 space-y-3'>
        <div className='flex flex-wrap gap-2'>
          {types.map((type) => (
            <button
              key={type.type_key}
              type='button'
              aria-pressed={!!visible[type.type_key]}
              className={cn(
                'text-xs px-2 py-1 rounded border border-border',
                visible[type.type_key] ? 'bg-accent text-accent-foreground' : 'opacity-50'
              )}
              onClick={() => toggleSeries(type.type_key)}
            >
              {attrLabel(t, type.type_key, type.name)}
            </button>
          ))}
        </div>
        <div className='flex-1 min-h-[200px] min-w-0'>
          <ResponsiveContainer width='100%' height='100%' minHeight={200}>
            <LineChart data={chartData}>
              <XAxis dataKey='weekLabel' tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              {types
                .filter((ty) => visible[ty.type_key])
                .map((type, i) => (
                  <Line
                    key={type.type_key}
                    type='monotone'
                    dataKey={type.type_key}
                    name={attrLabel(t, type.type_key, type.name)}
                    stroke={['hsl(var(--primary))', '#22c55e', '#eab308', '#ef4444', '#8b5cf6'][i % 5]}
                    dot={{ r: 3 }}
                    hide={!visible[type.type_key]}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default AttributeTrendsPanel;
